import { useState, useCallback, memo } from 'react';

import {
  Box,
  Icon,
  MenuList,
  MenuItem,
  useTheme,
  Typography,
  Tab as MuiTab,
  TabProps as MuiTabProps,
} from '@material-ui/core';
import clsx from 'clsx';
import { useHistory, useLocation } from 'react-router-dom';

import { LightTooltip } from 'components';
import { IndicatorChart } from 'generated';
import { useDataTestId, useBootstrapApp } from 'hooks';

import { useStyles } from './styles';

export type IndicatorConfigItem = {
  path: string;
  name: string;
  id: string;
};

interface TabProps extends MuiTabProps {
  value: string;
  label: string;
  indicators?: IndicatorChart[];
  onChangeIndicator(contextId: string, indicatorId: string): void;
  routesPathById: Record<string, any>;
  showTooltip?: boolean;
}

function TabWithoutMemo({
  value,
  indicators,
  label,
  onChangeIndicator,
  routesPathById,
  showTooltip = true,
  ...rest
}: TabProps): JSX.Element {
  const theme = useTheme();
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { getTestId } = useDataTestId();
  const { currentIndicator, entity, entityType, indicatorEventId } =
    useBootstrapApp();

  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const [cancelHover, setCancelHover] = useState<boolean>(false);

  const getIsActive = useCallback(
    (indicatorId: string) => {
      if (currentIndicator) {
        return currentIndicator.id === indicatorId;
      }
      return location.pathname === routesPathById[indicatorId];
    },
    [currentIndicator, location.pathname, routesPathById],
  );

  const handleOpenMenu = useCallback(() => {
    setMenuIsOpen(true);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuIsOpen(false);
  }, []);

  const handleCancelHover = useCallback(() => {
    setCancelHover(true);
    setTimeout(() => {
      setCancelHover(false);
    }, theme.transitions.duration.standard + 50);
  }, [theme.transitions.duration.standard]);

  const handleNavigate = useCallback(
    (indicatorId: string) => () => {
      const isSameIndicator = getIsActive(indicatorId);

      if (isSameIndicator) {
        return;
      }

      handleCancelHover();
      const search = `?entityId=${entity?.id}&entityType=${entityType}&indicatorEventId=${indicatorEventId}`;
      const path = routesPathById[indicatorId] || '/about';

      history.push(`${path}${search}`);
      onChangeIndicator(value, indicatorId);
      setMenuIsOpen(false);
    },
    [
      entity?.id,
      entityType,
      getIsActive,
      handleCancelHover,
      history,
      indicatorEventId,
      onChangeIndicator,
      routesPathById,
      value,
    ],
  );

  return (
    <>
      {showTooltip ? (
        <LightTooltip
          interactive={!cancelHover}
          open={menuIsOpen && !cancelHover}
          onOpen={handleOpenMenu}
          onClose={handleCloseMenu}
          classes={{
            tooltip: classes.tooltipPaper,
          }}
          placement="top"
          PopperProps={{
            popperOptions: {
              modifiers: {
                placement: 'bottom',
                offset: {
                  enabled: true,
                  offset: `0, 27% - ${theme.spacing(3)}px`,
                },
              },
            },
          }}
          title={
            <MenuList
              id={`menu-list-grow-${value}`}
              {...getTestId(`menu-${value}`)}
            >
              {!!indicators && !!indicators.length
                ? indicators.map(indicator => (
                    <MenuItem
                      color="primary"
                      key={indicator.id}
                      onClick={handleNavigate(indicator.id)}
                      {...getTestId(`indicator-${indicator.id}`)}
                    >
                      <Typography
                        className={clsx({
                          [classes.activeIndicator]: getIsActive(indicator.id),
                        })}
                        variant="subtitle2"
                        color="textSecondary"
                      >
                        {indicator.name}
                      </Typography>
                    </MenuItem>
                  ))
                : 'Sem dados'}
            </MenuList>
          }
        >
          <MuiTab
            value={value}
            aria-controls={menuIsOpen ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            className={classes.label}
            label={
              <Box display="flex" alignItems="center">
                <div style={{ height: 0.01, width: 0.01 }} />
                {label}{' '}
                <Icon
                  color="inherit"
                  fontSize="medium"
                  className={clsx(classes.icon, {
                    open: menuIsOpen,
                  })}
                >
                  expand_more
                </Icon>
              </Box>
            }
            {...rest}
          />
        </LightTooltip>
      ) : (
        <MuiTab
          value={value}
          className={classes.label}
          onClick={handleNavigate(value)}
          label={label}
          {...rest}
        />
      )}
    </>
  );
}

export const Tab = memo(TabWithoutMemo) as typeof TabWithoutMemo;
